/**
 * Client API: fetch con cookie di sessione e refresh automatico.
 * Se una richiesta riceve 401, tenta UNA volta il refresh e ripete la richiesta.
 */

export interface ApiFieldError {
  path: string;
  message: string;
}

export class ApiError extends Error {
  status: number;
  code: string;
  fields?: ApiFieldError[];

  constructor(status: number, code: string, message: string, fields?: ApiFieldError[]) {
    super(message);
    this.status = status;
    this.code = code;
    if (fields) this.fields = fields;
  }
}

let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  // Un solo refresh in volo alla volta, condiviso tra richieste concorrenti.
  refreshPromise ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

export async function api<T>(
  path: string,
  options: { method?: string; body?: unknown; retryOn401?: boolean } = {},
): Promise<T> {
  const { method = 'GET', body, retryOn401 = true } = options;

  const doFetch = () =>
    fetch(`/api${path}`, {
      method,
      credentials: 'include',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
      body: body !== undefined ? JSON.stringify(body) : null,
    });

  let res = await doFetch();

  if (res.status === 401 && retryOn401 && !path.startsWith('/auth/')) {
    const refreshed = await tryRefresh();
    if (refreshed) res = await doFetch();
    if (res.status === 401) {
      // Sessione irrecuperabile: avvisa l'app, che riporta al login
      // conservando la pagina di provenienza (vedi AuthContext).
      window.dispatchEvent(new CustomEvent('pc:unauthorized'));
    }
  }

  if (!res.ok) {
    let payload: { error?: string; message?: string; fields?: ApiFieldError[] } = {};
    try {
      payload = await res.json();
    } catch {
      // risposta non JSON
    }
    throw new ApiError(
      res.status,
      payload.error ?? 'UNKNOWN',
      payload.message ?? 'Qualcosa è andato storto. Riprova.',
      payload.fields,
    );
  }

  return res.json() as Promise<T>;
}
